import {
	Button,
	Checkbox,
	Col,
	Empty,
	Flex,
	Input,
	Row,
	Tooltip,
	notification,
} from 'antd';
import { Account, AccountStatus } from 'types/Account';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TextM, TextS } from 'components/Text';
import theme from 'theme/theme';
import EmptyListImage from 'components/images/emptyListB.png';
import {
	IoAddCircleOutline,
	IoArrowForwardSharp,
	IoChevronForward,
} from 'react-icons/io5';
import { LuSearch } from 'react-icons/lu';
import { AiOutlineStop } from 'react-icons/ai';
import { CiEdit } from 'react-icons/ci';
import { GoTrash } from 'react-icons/go';
import { FaCheck, FaRegCopy } from 'react-icons/fa6';
import { FaRegTrashCan } from 'react-icons/fa6';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { CreateAccountDestinationPixModal } from 'components/CreateAccountDestinationPixModal';
import { ConfirmationModal } from 'components/ConfirmationModal';
import SuccessModal from 'components/SuccessModal/SuccessModal';
import { EmptyFiltersResult } from 'components/EmptyFiltersResult';
import { FilterButton } from 'components/FilterButton';
import { Table } from 'components/Table';
import { PixKey, pixKeyOptions } from 'components/PixKeySelect/PixKeySelect';
import { useDestinations } from 'modules/escrow/hooks';
import { DestinationsService } from 'modules/escrow/services/destinations';
import {
	Destination,
	DestinationListItem,
	DestinationType,
} from 'types/Destination';
import { ESortingPage, PaginationInfo } from 'types/List';
import { useCurrentProfile } from 'modules/core/context/ProfileContext';
import { AccessPermission, AccessType } from 'types/Access';
import { useNavigate, useParams } from 'react-router-dom';
import { useIsEscrowAccountRoute } from 'modules/escrow/utils/adminRoute';
import { contaLivre } from 'modules/escrow/constants/routes';
import { BankListDescription } from 'constants/banksList';
import { DestinationTypeDescription } from 'constants/destination';
import { normalizeBankAccount, normalizeCpfCnpj } from 'helpers/normalizers';
import { sortTableHandler } from 'helpers/tables';
import {
	getPixNormalizer,
	normalizePixKeyIn,
	PixNormalizers,
} from 'modules/escrow/utils/normalizePix';
import EscrowDetailsAccountDestinationFilters from './EscrowDetailsAccountDestinationFilters';
import EscrowDetailsAccountDestinationDeleteConfirmation from './EscrowDetailsAccountDestinationDeleteConfirmation';
import EscrowDetailsAccountDestinationFilterChips, {
	FilterChipKey,
} from './EscrowDetailsAccountDestinationFilterChips';
import { EscrowDestinationsFiltersType } from './EscrowDetailsAccountDestination.types';
import {
	ButtonActions,
	FiltersContainer,
	FiltersGroup,
	SearchContainer,
} from './EscrowDetailsAccountDestination.styles';

const INITIAL_PAGINATION: PaginationInfo = {
	currentPage: 1,
	pageSize: 10,
};

const SEARCH_DEBOUNCE_MS = 500;

const COPY_FEEDBACK_MS = 3000;

const UNFINISHED_POSTINGS_TOOLTIP =
	'Este beneficiário possui uma transação pendente de aprovação.';

const ELLIPSIS_STYLE = {
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '150px',
} as const;

// o backend pode devolver a string literal "null" em campos opcionais
const NULLISH_TEXTS = ['null', 'undefined'];

const sanitizeText = (value?: string | null): string | undefined => {
	const trimmed = value?.trim();

	if (!trimmed || NULLISH_TEXTS.includes(trimmed.toLowerCase())) {
		return undefined;
	}

	return trimmed;
};

const orDash = (value?: string | null): string => sanitizeText(value) ?? '-';

const toDestination = (destination: DestinationListItem): Destination => ({
	id: destination.id,
	taxpayer_id: destination.taxpayer_id,
	name: destination.name,
	bank: destination.bank,
	branch: destination.branch,
	account: destination.account,
	type: destination.type,
	key: sanitizeText(destination.key),
	key_type: sanitizeText(destination.key_type),
});

interface IEscrowDetailsAccountDestinationProps {
	account: Account;
}
const EscrowDetailsAccountDestination: React.FC<
	IEscrowDetailsAccountDestinationProps
> = ({ account }) => {
	const [api, contextHolder] = notification.useNotification();
	const { type, permissions } = useCurrentProfile();
	const navigate = useNavigate();
	const { id } = useParams();

	const [isDestinationAdded, setIsDestinationAdded] =
		useState<boolean>(false);
	const [accountDestinationToEdit, setAccountDestinationToEdit] =
		useState<Destination>();
	const [copiedId, setCopiedId] = useState<string>();
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [destinationsToDelete, setDestinationsToDelete] = useState<string[]>(
		[],
	);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState<boolean>(false);
	const [isDeleteTokenOpen, setIsDeleteTokenOpen] = useState<boolean>(false);

	const [paginationInfo, setPaginationInfo] =
		useState<PaginationInfo>(INITIAL_PAGINATION);
	const [sorting, setSorting] = useState<{
		order: ESortingPage;
		sort: string;
	}>({ order: ESortingPage.DESC, sort: 'created_at' });
	const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
	const [filters, setFilters] = useState<EscrowDestinationsFiltersType>(
		{} as EscrowDestinationsFiltersType,
	);
	const [searchValue, setSearchValue] = useState<string>('');

	const isActiveUser = account.status === AccountStatus.ACTIVE;

	const isAbleToCreateBeneficiary =
		type === AccessType.FINANCIAL_INSTITUTION ||
		(permissions.includes(AccessPermission.CREATE_BENEFICIARY) &&
			isActiveUser);

	const [
		isNewAccountDestinationModalOpen,
		setIsNewAccountDestinationModalOpen,
	] = useState<boolean>(false);

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'accountDestinations',
			account.id,
			paginationInfo,
			filters,
			sorting,
		],
		queryFn: () =>
			DestinationsService.getDestinations(account.id, {
				page: paginationInfo.currentPage,
				size: paginationInfo.pageSize,
				order: sorting.order,
				sort: sorting.sort,
				id: filters.id,
				search: filters.search,
				account: filters.account,
				creator: filters.creator,
				bank: filters.bank,
				branch: filters.branch,
				type: filters.type,
				key_type: filters.key_type,
				created_at_start_date:
					filters.created_at_start_date || undefined,
				created_at_end_date: filters.created_at_end_date || undefined,
			}),
		enabled: !!account.id,
	});

	const hasFilters = Object.values(filters).some(value => !!value);

	// a busca principal dispara sozinha enquanto o usuario digita
	const debouncedCommitSearch = useMemo(
		() =>
			debounce((value: string) => {
				setFilters(prev => ({
					...prev,
					search: value.trim() || undefined,
				}));
				setPaginationInfo(INITIAL_PAGINATION);
			}, SEARCH_DEBOUNCE_MS),
		[],
	);

	useEffect(
		() => () => debouncedCommitSearch.cancel(),
		[debouncedCommitSearch],
	);

	// o campo de busca principal fica fora do drawer, entao o valor
	// digitado precisa ser mantido ao aplicar os filtros avancados
	const applyFilters = useCallback(
		(newFilters: EscrowDestinationsFiltersType) => {
			debouncedCommitSearch.cancel();
			setFilters({
				...newFilters,
				search: searchValue.trim() || undefined,
			});
			setPaginationInfo(INITIAL_PAGINATION);
		},
		[debouncedCommitSearch, searchValue],
	);

	const handleClearFilters = useCallback(() => {
		debouncedCommitSearch.cancel();
		setFilters({} as EscrowDestinationsFiltersType);
		setSearchValue('');
		setPaginationInfo(INITIAL_PAGINATION);
	}, [debouncedCommitSearch]);

	const handleRemoveFilter = useCallback(
		(key: FilterChipKey) => {
			if (key === 'search') {
				debouncedCommitSearch.cancel();
				setSearchValue('');
			}

			setFilters(prev =>
				key === 'created_at'
					? {
							...prev,
							created_at_start_date: undefined,
							created_at_end_date: undefined,
						}
					: { ...prev, [key]: undefined },
			);
			setPaginationInfo(INITIAL_PAGINATION);
		},
		[debouncedCommitSearch],
	);

	// a selecao vale so para a pagina exibida
	useEffect(() => {
		setSelectedIds([]);
	}, [paginationInfo, filters, sorting]);

	const clearCopyTimeout = useCallback(() => {
		if (copyTimeoutRef.current) {
			clearTimeout(copyTimeoutRef.current);
		}
	}, []);

	const handleCopyId = useCallback(
		(destinationId: string) => {
			navigator.clipboard.writeText(destinationId);
			setCopiedId(destinationId);

			clearCopyTimeout();
			copyTimeoutRef.current = setTimeout(
				() => setCopiedId(undefined),
				COPY_FEEDBACK_MS,
			);
		},
		[clearCopyTimeout],
	);

	useEffect(() => clearCopyTimeout, [clearCopyTimeout]);

	const handleModalSuccessClose = useCallback(() => {
		setIsDestinationAdded(true);
		setIsNewAccountDestinationModalOpen(false);
		setAccountDestinationToEdit(undefined);
	}, []);

	// cancelar preserva a selecao; so a exclusao concluida limpa
	const handleDeleteCancel = useCallback(() => {
		setDestinationsToDelete([]);
		setIsDeleteConfirmationOpen(false);
		setIsDeleteTokenOpen(false);
	}, []);

	const handleModalDeleteClose = useCallback(() => {
		handleDeleteCancel();
		setSelectedIds([]);
		setIsNewAccountDestinationModalOpen(false);
		setAccountDestinationToEdit(undefined);
	}, [handleDeleteCancel]);

	const handleModalClose = useCallback(() => {
		setIsNewAccountDestinationModalOpen(false);
		setAccountDestinationToEdit(undefined);
	}, []);

	const {
		createOrUpdateDestination,
		deleteDestinations,
		isBulkDeleting,
		loading,
	} = useDestinations(account.id || '', {
		onCreateSuccess: () => {
			handleModalSuccessClose();
			refetch();
		},
		onUpdateSuccess: () => {
			handleModalSuccessClose();
			refetch();
		},
		onBulkDeleteSuccess: result => {
			handleModalDeleteClose();
			refetch();

			// o lote nao e atomico: reporta o que nao pode ser removido
			if (result.failed.length > 0) {
				api.warning({
					description:
						result.failed[0]?.message ||
						'Alguns beneficiários não puderam ser removidos.',
					message: `${result.failed.length} de ${
						result.failed.length + result.deleted.length
					} não foram removidos.`,
				});
				return;
			}

			api.success({
				description:
					result.deleted.length > 1
						? `${result.deleted.length} contas beneficiárias foram removidas.`
						: 'A conta beneficiária foi removida.',
				message: 'Remoção concluída com sucesso.',
			});
		},
		onError: error => {
			handleModalClose();
			handleDeleteCancel();

			api.error({
				description: error?.data?.message || '',
				message: 'Ocorreu um problema com conta beneficiária.',
			});
		},
	});

	const openDeleteConfirmation = useCallback((destinationIds: string[]) => {
		if (destinationIds.length === 0) {
			return;
		}

		setDestinationsToDelete(destinationIds);
		setIsDeleteConfirmationOpen(true);
	}, []);

	const handleDelete = useCallback(
		(mfaToken: string) => {
			deleteDestinations(destinationsToDelete, mfaToken);
		},
		[deleteDestinations, destinationsToDelete],
	);

	const accountRoute = !useIsEscrowAccountRoute() ? contaLivre : 'escrow';

	const destinations = data?.content;

	// select all cobre apenas a pagina atual, ignorando os bloqueados
	const selectableIds = useMemo(
		() =>
			(destinations || [])
				.filter(destination => !destination.has_unfinished_postings)
				.map(destination => destination.id),
		[destinations],
	);

	const isAllSelected =
		selectableIds.length > 0 &&
		selectableIds.every(id => selectedIds.includes(id));

	const toggleDestination = useCallback(
		(destinationId: string, checked: boolean) => {
			setSelectedIds(prev =>
				checked
					? [...prev, destinationId]
					: prev.filter(id => id !== destinationId),
			);
		},
		[],
	);

	const columns: ColumnsType<DestinationListItem> = useMemo(() => {
		const items: ColumnsType<DestinationListItem> = [
			{
				title: 'ID',
				width: '12%',
				render: (destination: DestinationListItem) => {
					const isCopied = copiedId === destination.id;

					return (
						<Flex align="center" gap={4}>
							<Tooltip title={sanitizeText(destination.id) ?? ''}>
								<TextM
									weight="normal"
									style={{
										...ELLIPSIS_STYLE,
										maxWidth: '7rem',
									}}
								>
									{orDash(destination.id)}
								</TextM>
							</Tooltip>
							<Tooltip
								title={isCopied ? 'Copiado!' : 'Copiar ID'}
							>
								<Button
									type="text"
									size="small"
									onClick={() => handleCopyId(destination.id)}
								>
									{isCopied ? (
										<FaCheck
											color={theme.successHighlight}
										/>
									) : (
										<FaRegCopy color={theme.primary} />
									)}
								</Button>
							</Tooltip>
						</Flex>
					);
				},
			},
			{
				title: 'Beneficiário',
				width: '20%',
				sorter: true,
				key: 'name',
				render: (destination: DestinationListItem) => (
					<div>
						<Tooltip title={sanitizeText(destination.name) ?? ''}>
							<TextM weight="bold" style={ELLIPSIS_STYLE}>
								{orDash(destination.name)}
							</TextM>
						</Tooltip>
						<TextS weight="normal" color={theme.textSecondary}>
							{orDash(
								normalizeCpfCnpj(
									sanitizeText(destination.taxpayer_id),
								),
							)}
						</TextS>
					</div>
				),
			},
			{
				title: 'Banco',
				width: '15%',
				sorter: true,
				key: 'bank',
				render: (destination: DestinationListItem) => {
					const bank = sanitizeText(destination.bank);
					const bankName = bank
						? BankListDescription[bank] || bank
						: undefined;

					return (
						<Tooltip title={bankName ?? ''}>
							<TextM weight="normal" style={ELLIPSIS_STYLE}>
								{orDash(bankName)}
							</TextM>
						</Tooltip>
					);
				},
			},
			{
				title: 'Agência',
				width: '8%',
				sorter: true,
				key: 'branch',
				render: (destination: DestinationListItem) => (
					<TextM weight="normal">
						{orDash(
							sanitizeText(destination.branch)?.padStart(4, '0'),
						)}
					</TextM>
				),
			},
			{
				title: 'Conta',
				width: '10%',
				sorter: true,
				key: 'account',
				render: (destination: DestinationListItem) => (
					<TextM weight="normal">
						{orDash(
							normalizeBankAccount(
								sanitizeText(destination.account) ?? '',
							),
						)}
					</TextM>
				),
			},
			{
				title: 'Tipo de conta',
				width: '12%',
				render: (destination: DestinationListItem) => (
					<TextM weight="normal">
						{orDash(
							DestinationTypeDescription[
								destination.type as DestinationType
							],
						)}
					</TextM>
				),
			},
			{
				title: 'Chave Pix',
				width: '15%',
				render: (destination: DestinationListItem) => {
					const pixKey = sanitizeText(destination.key);
					const keyType = sanitizeText(destination.key_type);

					if (!pixKey || !keyType) {
						return <TextM weight="normal">-</TextM>;
					}

					const pixKeyType =
						keyType.includes('CPF') || keyType.includes('CNPJ')
							? PixKey.TAXPAYER_ID
							: (keyType as PixKey);

					const pixKeyValue = getPixNormalizer(
						keyType as keyof typeof PixNormalizers,
					)(normalizePixKeyIn(pixKey, keyType as PixKey));

					return (
						<div>
							<Tooltip title={sanitizeText(pixKeyValue) ?? ''}>
								<TextM weight="normal" style={ELLIPSIS_STYLE}>
									{orDash(pixKeyValue)}
								</TextM>
							</Tooltip>
							<TextS weight="normal" color={theme.textSecondary}>
								{orDash(pixKeyOptions[pixKeyType])}
							</TextS>
						</div>
					);
				},
			},
			{
				title: 'Criador do beneficiário',
				width: '15%',
				sorter: true,
				key: 'creator.fullName',
				render: (destination: DestinationListItem) => (
					<Tooltip
						title={
							sanitizeText(destination.creator?.full_name) ?? ''
						}
					>
						<TextM weight="normal" style={ELLIPSIS_STYLE}>
							{orDash(destination.creator?.full_name)}
						</TextM>
					</Tooltip>
				),
			},
			{
				title: 'Data de cadastro',
				width: '12%',
				sorter: true,
				key: 'created_at',
				render: (destination: DestinationListItem) => {
					const createdAtValue = sanitizeText(destination.created_at);
					const createdAt = createdAtValue
						? dayjs(createdAtValue)
						: undefined;

					if (!createdAt?.isValid()) {
						return <TextM weight="bold">-</TextM>;
					}

					return (
						<div>
							<TextM weight="bold">
								{createdAt.format('DD/MM/YYYY')}
							</TextM>
							<TextS weight="normal" color={theme.textSecondary}>
								{`às ${createdAt.format('HH:mm:ss')}`}
							</TextS>
						</div>
					);
				},
			},
		];

		if (isAbleToCreateBeneficiary) {
			items.unshift({
				title: () => (
					<Checkbox
						checked={isAllSelected}
						indeterminate={!isAllSelected && selectedIds.length > 0}
						disabled={selectableIds.length === 0}
						onChange={event =>
							setSelectedIds(
								event.target.checked ? selectableIds : [],
							)
						}
					/>
				),
				width: '4%',
				render: (destination: DestinationListItem) => {
					const checkbox = (
						<Checkbox
							checked={selectedIds.includes(destination.id)}
							disabled={destination.has_unfinished_postings}
							onChange={event =>
								toggleDestination(
									destination.id,
									event.target.checked,
								)
							}
						/>
					);

					// tooltip precisa de wrapper: elemento desabilitado
					// nao dispara eventos de mouse
					return destination.has_unfinished_postings ? (
						<Tooltip title={UNFINISHED_POSTINGS_TOOLTIP}>
							<span style={{ display: 'inline-flex' }}>
								{checkbox}
							</span>
						</Tooltip>
					) : (
						checkbox
					);
				},
			});

			items.push({
				title: 'Ações',
				align: 'center',
				width: '8%',
				render: (destination: DestinationListItem) => (
					<ButtonActions>
						<Button
							type="text"
							disabled={loading}
							onClick={() => {
								setIsNewAccountDestinationModalOpen(true);
								setAccountDestinationToEdit(
									toDestination(destination),
								);
							}}
						>
							<CiEdit />
						</Button>
						<Tooltip
							title={
								destination.has_unfinished_postings
									? UNFINISHED_POSTINGS_TOOLTIP
									: ''
							}
						>
							<span style={{ display: 'inline-flex' }}>
								<Button
									type="text"
									disabled={
										loading ||
										destination.has_unfinished_postings
									}
									onClick={() =>
										openDeleteConfirmation([destination.id])
									}
								>
									<GoTrash />
								</Button>
							</span>
						</Tooltip>
					</ButtonActions>
				),
			});
		}

		return items;
	}, [
		isAbleToCreateBeneficiary,
		loading,
		selectedIds,
		selectableIds,
		isAllSelected,
		toggleDestination,
		openDeleteConfirmation,
		copiedId,
		handleCopyId,
	]);

	const hasNoDestinations =
		!isLoading && !hasFilters && destinations?.length === 0;

	return (
		<>
			{contextHolder}
			{hasNoDestinations ? (
				<>
					<Flex
						style={{ marginTop: '2rem', flexDirection: 'column' }}
						justify="center"
						align="center"
					>
						<TextM weight="bold">
							Nenhuma conta beneficiárias cadastrada.
						</TextM>
						<TextM weight="bold">
							Atenção! você só pode adicionar contas beneficiárias
							antes da análise de abertura da conta.
						</TextM>
						<TextS
							style={{
								marginTop: '0.5rem',
								color: theme.textGray,
							}}
						>
							Para adicionar contas beneficiárias após a análise,
							entre em contato com a Celcoin.
						</TextS>

						<Empty
							style={{
								marginTop: '1rem',
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
							}}
							description=""
							image={EmptyListImage}
							imageStyle={{
								height: 240,
							}}
						/>

						{isAbleToCreateBeneficiary && (
							<>
								<Button
									type="primary"
									onClick={() => {
										setIsNewAccountDestinationModalOpen(
											true,
										);
									}}
									style={{
										marginTop: '2rem',
										width: 'fit-content',
										padding: '0.5rem 1rem',
									}}
								>
									<Flex>
										<TextM
											style={{
												color: theme.white,
											}}
										>
											Adicionar conta beneficiária
										</TextM>
										<IoArrowForwardSharp
											size={'1.4rem'}
											style={{ width: '2rem' }}
											color={theme.white}
										/>
									</Flex>
								</Button>
								<Button
									type="default"
									onClick={() =>
										navigate(
											`/${accountRoute}/${id}/natural-person/details/imports/destination`,
										)
									}
									icon={
										<IoChevronForward
											size={'1rem'}
											color={theme.primary}
										/>
									}
									iconPosition="end"
									style={{
										marginTop: '1rem',
										width: 'fit-content',
										padding: '0.5rem 1rem',
									}}
								>
									<TextS color={theme.primary}>
										Cadastros em lote
									</TextS>
								</Button>
							</>
						)}
					</Flex>
				</>
			) : (
				<Flex vertical wrap={false} gap={16} justify="start">
					{isAbleToCreateBeneficiary && (
						<Row justify="end" style={{ gap: '1rem' }}>
							<Button
								type="default"
								onClick={() =>
									navigate(
										`/${accountRoute}/${id}/natural-person/details/imports/destination`,
									)
								}
								icon={
									<IoChevronForward
										size={'1rem'}
										color={theme.primary}
									/>
								}
								iconPosition="end"
							>
								<TextS color={theme.primary}>
									Cadastros em lote
								</TextS>
							</Button>
							<Button
								type="primary"
								onClick={() => {
									setIsNewAccountDestinationModalOpen(true);
								}}
							>
								<TextS color={theme.white}>
									Adicionar conta beneficiária
								</TextS>
								<IoAddCircleOutline
									size={'1.4rem'}
									style={{ width: '2rem' }}
									color={theme.white}
								/>
							</Button>
						</Row>
					)}
					<Row gutter={[16, 16]}>
						<Col span={24}>
							<FiltersContainer>
								<FiltersGroup>
									<SearchContainer>
										<Input
											placeholder="Buscar por beneficiário"
											prefix={<LuSearch size={18} />}
											value={searchValue}
											allowClear
											onChange={event => {
												setSearchValue(
													event.target.value,
												);
												debouncedCommitSearch(
													event.target.value,
												);
											}}
										/>
									</SearchContainer>
									<FilterButton
										icon={<LuSearch size={18} />}
										onClick={() => setIsFiltersOpen(true)}
									>
										<TextS color={theme.white}>
											Busca avançada
										</TextS>
									</FilterButton>
									<FilterButton
										icon={
											<AiOutlineStop
												size={18}
												color={theme.white}
											/>
										}
										variation="secondary"
										onClick={handleClearFilters}
										disabled={!hasFilters}
									>
										<TextS style={{ color: theme.white }}>
											Limpar filtros
										</TextS>
									</FilterButton>
								</FiltersGroup>
								{selectedIds.length > 0 && (
									<Button
										type="primary"
										style={{
											backgroundColor: theme.danger,
											borderColor: theme.danger,
										}}
										onClick={() =>
											openDeleteConfirmation(selectedIds)
										}
									>
										<TextS color={theme.white}>
											Excluir selecionados
										</TextS>
										<FaRegTrashCan color={theme.white} />
									</Button>
								)}
							</FiltersContainer>
							<EscrowDetailsAccountDestinationFilterChips
								filters={filters}
								onRemove={handleRemoveFilter}
							/>
							{!isLoading && destinations?.length === 0 ? (
								<EmptyFiltersResult
									hasBorder
									title="Nenhuma conta beneficiária encontrada."
									description={
										'Não encontramos resultados para os filtros aplicados.\nTente ajustar a busca.'
									}
								/>
							) : (
								<Table
									scroll={{ x: 1400 }}
									columns={columns}
									rowKey={record => record.id}
									dataSource={destinations}
									loading={isLoading}
									onChange={(_, __, sorter) =>
										sortTableHandler(sorter, setSorting)
									}
									pagination={{
										total: data?.total_elements || 0,
										showTotal(total) {
											const currentSize =
												paginationInfo.currentPage *
												paginationInfo.pageSize;
											return `${
												currentSize > total
													? total
													: currentSize
											} de ${total}`;
										},
										pageSizeOptions: [
											'10',
											'30',
											'60',
											'90',
										],
										showSizeChanger: true,
										pageSize: paginationInfo.pageSize,
										current: paginationInfo.currentPage,
										onChange(page, pageSize) {
											setPaginationInfo({
												currentPage: page,
												pageSize,
											});
										},
									}}
								/>
							)}
						</Col>
					</Row>
				</Flex>
			)}
			<EscrowDetailsAccountDestinationFilters
				isOpen={isFiltersOpen}
				filters={filters}
				onClose={() => setIsFiltersOpen(false)}
				onApply={newFilters => {
					applyFilters(newFilters);
					setIsFiltersOpen(false);
				}}
			/>
			{(isNewAccountDestinationModalOpen || accountDestinationToEdit) && (
				<CreateAccountDestinationPixModal
					isOpen={
						isNewAccountDestinationModalOpen ||
						!!accountDestinationToEdit
					}
					onClose={() => {
						setIsNewAccountDestinationModalOpen(false);
						setAccountDestinationToEdit(undefined);
					}}
					onCreateOrUpdate={destination => {
						createOrUpdateDestination({
							...destination,
							...(destination?.key &&
							destination.key.trim() !== ''
								? {
										key: destination.key,
										key_type: destination.key_type,
									}
								: {
										key: undefined,
										key_type: undefined,
										id_end_to_end: undefined,
									}),
						});
					}}
					accountId={account.id}
					isLoading={loading}
					accountDestination={accountDestinationToEdit}
				/>
			)}

			{isDestinationAdded && (
				<SuccessModal
					closeModal={() => setIsDestinationAdded(false)}
					message={'Conta Beneficiária adicionada com sucesso!'}
					title={'Conta Beneficiária adicionada'}
				/>
			)}

			<ConfirmationModal
				isOpen={isDeleteConfirmationOpen}
				danger={true}
				title="Remover conta beneficiária"
				confirmText="Remover conta"
				cancelText="Cancelar"
				onConfirm={() => {
					setIsDeleteConfirmationOpen(false);
					setIsDeleteTokenOpen(true);
				}}
				onCancel={handleDeleteCancel}
				onClose={handleDeleteCancel}
			>
				<TextM weight="normal">
					{destinationsToDelete.length > 1
						? `Você tem certeza que deseja remover as ${destinationsToDelete.length} contas beneficiárias selecionadas?`
						: 'Você tem certeza que deseja remover essa conta beneficiária?'}
				</TextM>
			</ConfirmationModal>

			<EscrowDetailsAccountDestinationDeleteConfirmation
				isOpen={isDeleteTokenOpen}
				isLoading={isBulkDeleting}
				message={`Para sua segurança, realize a autenticação do seu dispositivo para excluir ${destinationsToDelete.length} beneficiário(s).`}
				onClose={handleDeleteCancel}
				onConfirm={handleDelete}
			/>
		</>
	);
};

export default EscrowDetailsAccountDestination;
